import React, { useEffect, useState } from 'react';
import { funds as fundsApi } from '../services/api';
import { Sparkles, Gavel, ShoppingBag, PackageOpen } from 'lucide-react';

const ICON = {
  auction: Gavel,
  auction_silent: Gavel,
  dealbin: ShoppingBag,
  refund: PackageOpen,
};

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function displayName(name) {
  if (!name) return 'Someone';
  const n = String(name);
  if (n.startsWith('test_') || n === 'Someone') return 'Someone';
  return n;
}

function verbFor(source) {
  if (source === 'dealbin') return 'grabbed';
  if (source === 'auction_silent') return 'won a silent bid on';
  return 'won';
}

function amountFor(row) {
  if ((row.amountUsd || 0) > 0) return `$${row.amountUsd.toFixed(2)}`;
  return `${(row.amountPts || 0).toLocaleString()} pts`;
}

function splitLabel(row) {
  if ((row.amountUsd || 0) > 0) {
    const half = row.amountUsd / 2;
    return `$${half.toFixed(2)} community · $${half.toFixed(2)} youth`;
  }
  const half = Math.floor((row.amountPts || 0) / 2);
  return `${half.toLocaleString()} pts community · ${half.toLocaleString()} pts youth`;
}

export default function ActivityTicker({ limit = 8, refreshMs = 30000 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const d = await fundsApi.recent(limit);
        if (alive) { setRows(Array.isArray(d) ? d : []); setLoading(false); }
      } catch (e) { if (alive) setLoading(false); }
    };
    load();
    const id = setInterval(load, refreshMs);
    return () => { alive = false; clearInterval(id); };
  }, [limit, refreshMs]);

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div data-testid="activity-ticker" className="pbb-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">
        <Sparkles size={12} className="text-green-400" /> Recent community activity
      </div>
      <ul className="divide-y divide-[#1a1a1a]">
        {rows.map((r) => {
          const Icon = ICON[r.source] || Gavel;
          return (
            <li key={r.id} data-testid={`activity-row-${r.id}`} className="flex items-center gap-3 py-2.5">
              <div className="shrink-0 w-8 h-8 rounded-full bg-green-500/10 border border-green-500/25 flex items-center justify-center text-green-400">
                <Icon size={14} />
              </div>
              <div className="min-w-0 flex-1 text-sm">
                <div className="text-white truncate">
                  <span className="font-semibold">{displayName(r.buyerFirstName)}</span>
                  <span className="text-gray-300"> {verbFor(r.source)} </span>
                  <span className="font-medium">{r.title}</span>
                </div>
                <div className="text-[11px] text-gray-500">{splitLabel(r)} · {timeAgo(r.createdAt)}</div>
              </div>
              <div className="shrink-0 text-sm font-semibold text-green-400">{amountFor(r)}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
